/**
 * usePushRegistration — iter82.
 *
 * Wires the browser to Firebase Web Push:
 *  1. Waits until the current user is loaded and NOT anonymous.
 *  2. Reads the Firebase Web config from CRA env (`REACT_APP_FIREBASE_*`).
 *  3. Registers /firebase-messaging-sw.js (query-string carries the config).
 *  4. Asks the user for permission ONCE — never re-prompts on refusal.
 *  5. Calls `getToken(messaging, { vapidKey })` and POSTs it to the
 *     backend's `/push/register-token`, tagged `platform: "web"`.
 *  6. Refreshes the token on demand and re-registers if it rotates.
 *
 * The hook is fully dormant when any of these is true:
 *   - The browser doesn't support Notifications / Push / Service Workers.
 *   - The user has push disabled in their notification preferences.
 *   - The Firebase Web config is not populated (e.g. Preview environment).
 *   - Permission was previously denied.
 *
 * Nothing here throws to the outer app; every failure is logged and
 * swallowed so a broken FCM setup never blocks the UI.
 */
import { useEffect, useRef } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

function isConfigured() {
  return !!(cfg.apiKey && cfg.projectId && cfg.messagingSenderId && cfg.appId && VAPID_KEY);
}

function detectPlatform() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "web";
}

function swQueryString() {
  const q = new URLSearchParams({
    apiKey: cfg.apiKey,
    authDomain: cfg.authDomain,
    projectId: cfg.projectId,
    messagingSenderId: cfg.messagingSenderId,
    appId: cfg.appId,
  });
  return `/firebase-messaging-sw.js?${q.toString()}`;
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    // Query string version guarantees a re-registration when the config changes.
    return await navigator.serviceWorker.register(swQueryString(), { scope: "/" });
  } catch (e) {
    console.warn("[push] SW registration failed", e);
    return null;
  }
}

async function ensureFirebaseApp() {
  if (getApps().length) return getApp();
  return initializeApp(cfg);
}

async function fetchTokenAndRegister(swRegistration) {
  const app = await ensureFirebaseApp();
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swRegistration || undefined,
  });
  if (!token) return null;
  try {
    await api.post("/push/register-token", {
      token,
      platform: detectPlatform(),
      device_info: (navigator.userAgent || "").slice(0, 180),
    });
    try { localStorage.setItem("push_token", token); } catch {}
  } catch (e) {
    console.warn("[push] register-token failed", e?.response?.status);
    return null;
  }
  // Foreground messages: if the tab is focused, browsers by default do NOT
  // show a system notification. We surface a light in-app hint using the
  // existing Sonner toast when a push arrives while the app is visible.
  try {
    onMessage(messaging, (payload) => {
      window.dispatchEvent(new CustomEvent("toclean:push", { detail: payload }));
    });
  } catch {}
  return token;
}

export default function usePushRegistration() {
  const { user, loading } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (loading || !user?.user_id) return;
      if (attempted.current) return;
      if (!isConfigured()) return; // dormant on Preview / missing keys
      // Global support check (throws on older Safari without Push).
      let supported = false;
      try { supported = await isSupported(); } catch { supported = false; }
      if (!supported) return;
      if (typeof Notification === "undefined") return;
      if (Notification.permission === "denied") return;
      // Respect the user's own preferences from /auth/me → notifications.push
      const prefs = (user.notifications || user.notification_preferences || {});
      if (prefs.push === false || prefs.push_enabled === false) return;
      attempted.current = true;
      try {
        if (Notification.permission === "default") {
          const asked = await Notification.requestPermission();
          if (asked !== "granted") return;
        }
        const swReg = await registerServiceWorker();
        if (cancelled) return;
        await fetchTokenAndRegister(swReg);
      } catch (e) {
        console.warn("[push] setup failed", e);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [loading, user?.user_id]);

  // Listen for push-click messages from the service worker to enable
  // deep-linking from a background notification.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      const data = event.data || {};
      if (data.type === "push-click" && data.link) {
        try {
          const url = new URL(data.link, window.location.origin);
          if (url.origin === window.location.origin) {
            window.history.pushState({}, "", url.pathname + url.search + url.hash);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        } catch {}
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);
}
